import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, ShoppingCart, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ToolSelector from '../components/ToolSelector';
import PageTutorial from '../components/PageTutorial';
import PageHero from '../components/PageHero';
import AdDisplay from '../components/AdDisplay';
import { ProductCard, StoreFilters, ProductDetailModal } from '../components/store';

const Store = () => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [selectedCategory, setSelectedCategory] = useState('All');
 const [selectedMachine, setSelectedMachine] = useState('All');
 const [showOnSale, setShowOnSale] = useState(false);
 const [selectedProduct, setSelectedProduct] = useState(null);
 const [showProductModal, setShowProductModal] = useState(false);
 const [partners, setPartners] = useState([]);
 const [currency, setCurrency] = useState(() => {
 // Try to get from localStorage first
 const saved = localStorage.getItem('preferredCurrency');
 if (saved) return saved;
 // Try to detect location (defaults to USD if detection fails)
 try {
 const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
 return timezone.includes('Toronto') || timezone.includes('Vancouver') || 
 timezone.includes('Montreal') || timezone.includes('Edmonton') ? 'CAD' : 'USD';
 } catch {
 return 'USD';
 }
 });
 const [usdRate, setUsdRate] = useState(0.74);

 useEffect(() => {
 fetchStoreItems();
 fetchCurrencySettings();
 fetchPartners();
 }, []);

 useEffect(() => {
 // Save currency preference to localStorage
 localStorage.setItem('preferredCurrency', currency);
 }, [currency]);

 const fetchStoreItems = async () => {
 try {
 const response = await axios.get(`${API}/store`);
 setItems(response.data);
 } catch (error) {
 toast.error('Failed to load store');
 } finally {
 setLoading(false);
 }
 };

 const fetchCurrencySettings = async () => {
 try {
 const response = await axios.get(`${API}/currency-settings`);
 setUsdRate(response.data.usd_rate);
 } catch (error) {
 console.error('Failed to fetch currency settings');
 }
 };

 const fetchPartners = async () => {
 try {
 const response = await axios.get(`${API}/partners`);
 setPartners(response.data);
 } catch (error) {
 console.error('Failed to fetch partners');
 }
 };

 const formatPrice = (cadPrice) => {
 if (currency === 'CAD') {
 return `$${cadPrice.toFixed(2)} CAD`;
 } else {
 const usdPrice = (cadPrice * usdRate).toFixed(2);
 return `$${usdPrice} USD`;
 }
 };

 const openProductDetail = (product) => {
 setSelectedProduct(product);
 setShowProductModal(true);
 };

 // Filter items based on selected category, machine, and sale status
 const filteredItems = items.filter(item => {
 const categoryMatch = selectedCategory === 'All' || item.category === selectedCategory;
 const machineMatch = selectedMachine === 'All' || item.machine_name === selectedMachine;
 const saleMatch = !showOnSale || item.on_sale;
 return categoryMatch && machineMatch && saleMatch;
 });

 return (
 <div className="min-h-screen bg-background">
 <PageHero
 eyebrow="Curated marketplace"
 eyebrowIcon={ShoppingCart}
 title={<>Freeze-dry <span className="text-amber-brand">store</span></>}
 description="Trays, mylar, absorbers and machines we actually trust — no marketplace noise."
 backTo="/dashboard"
 backLabel="Dashboard"
 testId="store-hero"
 actions={
 <div className="flex gap-2">
 <Button
 variant={currency === 'CAD' ? 'default' : 'outline'}
 size="sm"
 onClick={() => setCurrency('CAD')}
 className={currency === 'CAD' ? 'bg-primary text-primary-foreground' : 'text-background border-background/30 bg-background/10 hover:bg-background/20'}
 >
 CAD
 </Button>
 <Button
 variant={currency === 'USD' ? 'default' : 'outline'}
 size="sm"
 onClick={() => setCurrency('USD')}
 className={currency === 'USD' ? 'bg-primary text-primary-foreground' : 'text-background border-background/30 bg-background/10 hover:bg-background/20'}
 >
 USD
 </Button>
 </div>
 }
 />

 <ToolSelector />

 <div className="container mx-auto px-4 pt-4">
   <AdDisplay location="store_banner" className="flex justify-center mb-4" />
 </div>

 <div className="container mx-auto px-4 py-8">
 <PageTutorial id="store" />
 {/* Disclaimer Banner */}
 <div className="mb-6 p-4 rounded-2xl border border-primary/40 bg-primary/5">
 <p className="text-sm text-foreground/80 leading-relaxed">
 <strong>⚠️ Third-Party Marketplace Notice:</strong> This store features products from external sellers. 
 <strong> Peak and Prairie Industries</strong>, <strong>Freeze Dry Academy</strong>, and their affiliates are not responsible for 
 items purchased, shipping, quality, or customer service from third-party vendors. All transactions occur on the seller's website. 
 Please review each seller's policies before purchasing.
 </p>
 </div>

 {/* Header Advertisement Space */}
 <div className="mb-6 p-6 bg-pro-soft rounded-lg border border-blue-200 dark:border-blue-800 text-center">
 <p className="text-sm text-slate-600 dark:text-neutral-400 mb-2">📢 Advertisement</p>
 <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
 Featured: Premium Freeze Drying Equipment
 </h3>
 <p className="text-slate-600 dark:text-neutral-400">
 Discover the best deals on freeze dryers, accessories, and supplies from trusted partners
 </p>
 </div>

 {/* Partners Section */}
 {partners.length > 0 && (
 <Card className="mb-8 shadow-lg">
 <CardHeader>
 <CardTitle>🤝 Our Trusted Partners</CardTitle>
 <CardDescription>
 We work with these verified vendors to bring you quality freeze drying products and services
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
 {partners.map(partner => (
 <div 
 key={partner.id}
 className="p-4 border rounded-lg hover:shadow-md transition-all cursor-pointer bg-white dark:bg-slate-800"
 onClick={() => partner.link && window.open(partner.link, '_blank')}
 >
 {partner.logo ? (
 <div className="h-16 flex items-center justify-center mb-3">
 <img 
 src={partner.logo} 
 alt={partner.name}
 className="max-h-full max-w-full object-contain"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.parentElement.innerHTML = `<div class="text-center font-semibold text-slate-700 dark:text-neutral-300">${partner.name}</div>`;
 }}
 />
 </div>
 ) : (
 <div className="h-16 flex items-center justify-center mb-3">
 <p className="font-semibold text-slate-700 dark:text-neutral-300 text-center">{partner.name}</p>
 </div>
 )}
 {partner.description && (
 <p className="text-xs text-slate-600 dark:text-neutral-400 text-center line-clamp-2">
 {partner.description}
 </p>
 )}
 {partner.region && (
 <p className="text-xs text-slate-500 dark:text-neutral-500 text-center mt-1">
 📍 {partner.region}
 </p>
 )}
 {partner.link && (
 <div className="mt-2 flex justify-center">
 <ExternalLink className="w-4 h-4 text-primary dark:text-blue-400" />
 </div>
 )}
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 <StoreFilters
 selectedCategory={selectedCategory}
 setSelectedCategory={setSelectedCategory}
 selectedMachine={selectedMachine}
 setSelectedMachine={setSelectedMachine}
 showOnSale={showOnSale}
 setShowOnSale={setShowOnSale}
 currency={currency}
 setCurrency={setCurrency}
 filteredCount={filteredItems.length}
 totalCount={items.length}
 />

 {loading ? (
 <div className="text-center py-12">
 <p className="text-slate-500">Loading store...</p>
 </div>
 ) : filteredItems.length === 0 ? (
 <Card className="text-center py-12">
 <CardContent>
 <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
 <p className="text-slate-500">No items available yet</p>
 </CardContent>
 </Card>
 ) : (
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
 {filteredItems.map(item => (
 <ProductCard
 key={item.id}
 item={item}
 formatPrice={formatPrice}
 onCardClick={openProductDetail}
 />
 ))}
 </div>
 </div>
 )}

 {/* Footer Advertisement Space */}
 <div className="mt-8 p-6 bg-pro-soft rounded-lg border border-primary/20 text-center">
 <p className="text-sm text-slate-600 dark:text-neutral-400 mb-2">📢 Advertisement</p>
 <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
 Join Our Freeze Drying Community
 </h3>
 <p className="text-slate-600 dark:text-neutral-400 mb-4">
 Learn techniques, share recipes, and connect with fellow enthusiasts
 </p>
 <Button onClick={() => navigate('/learning')} className="bg-primary hover:bg-primary/90">
 Explore Courses
 </Button>
 </div>

 {/* Footer Disclaimer */}
 <div className="mt-6 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-700">
 <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
 ⚖️ Legal Disclaimer
 </h4>
 <p className="text-xs text-slate-600 dark:text-neutral-400 leading-relaxed">
 <strong>Third-Party Marketplace:</strong> All products listed in this store are sold by independent third-party vendors. 
 When you click "Buy Now," you will be redirected to the seller's website to complete your purchase. 
 <strong> Peak and Prairie Industries</strong>, <strong>Freeze Dry Academy</strong>, and their affiliates do not manufacture, 
 ship, warranty, or provide customer service for these products. We are not responsible for product quality, shipping delays, 
 returns, refunds, or any issues arising from transactions with third-party sellers. By purchasing through these links, 
 you acknowledge that your transaction is solely between you and the third-party seller. Please review each seller's terms, 
 conditions, and return policies before making a purchase. We may earn a commission on qualifying purchases at no additional cost to you.
 </p>
 </div>
 </div>

 {/* Product Detail Modal */}
 {/* Product Detail Modal */}
 <ProductDetailModal
 product={selectedProduct}
 isOpen={showProductModal}
 onClose={() => setShowProductModal(false)}
 formatPrice={formatPrice}
 currency={currency}
 setCurrency={setCurrency}
 />
 </div>
 );
};

export default Store;
